import type { TopicMessage } from '@src/shared/websocket';
import type { AbsenceEditingLock } from '../types';

export type EditingSnapshot = {
  /** Server clock when the set was read; 0 when it could not be stamped. */
  readAt: number;
  locks: AbsenceEditingLock[];
  /** Present only on the frame sent when a socket connects: the id this tab
   * claims its locks under, so closing it releases them. */
  connectionId?: string;
};

/**
 * Read an `absence.editing` frame.
 *
 * Every message carries the whole set of held locks rather than a transition,
 * so a duplicate costs nothing and a lost one is repaired by the next. A frame
 * that is not a list of locks is rejected outright rather than partly applied:
 * a half-read snapshot would claim somebody had stopped editing.
 *
 * `readAt` is what orders them. Reading and publishing are separate steps on
 * the server, so two lock operations racing can deliver the older snapshot
 * last -- and applying that one would leave every client believing a lock is
 * held that has been given back, with nothing to correct them.
 */
export function parseEditingSnapshot(
  message: TopicMessage,
): EditingSnapshot | null {
  const { locks, read_at: readAt, connection_id: connectionId } = message;
  if (!Array.isArray(locks)) return null;

  const parsed: AbsenceEditingLock[] = [];
  for (const entry of locks) {
    if (!entry || typeof entry !== 'object') return null;
    const {
      target_user: targetUserId,
      owner_user: ownerUserId,
      owner_name: ownerName,
    } = entry as Record<string, unknown>;
    if (typeof targetUserId !== 'number') return null;
    if (typeof ownerUserId !== 'number') return null;
    parsed.push({
      targetUserId,
      ownerUserId,
      ownerName: typeof ownerName === 'string' ? ownerName : '',
    });
  }
  return {
    readAt: typeof readAt === 'number' ? readAt : 0,
    locks: parsed,
    ...(typeof connectionId === 'string' ? { connectionId } : {}),
  };
}

/** Who has a form open on this employee, if anybody. */
export function findAbsenceEditor(
  locks: AbsenceEditingLock[],
  targetUserId: number | null | undefined,
): AbsenceEditingLock | null {
  if (targetUserId == null) return null;
  return locks.find((lock) => lock.targetUserId === targetUserId) ?? null;
}
